import { OFFICE_NAMES, OfficeType } from 'domain/shared/employee/officeService';
import { TARGET_AUDIENCE_ALL_EMPLOYEES } from '../constants';
import { RelatedEmployeeFilter } from '../types';
import { PositionType } from 'domain/shared/employee/position/positionService';
import { EMPLOYEE_CONTRACT_TYPES } from 'domain/shared/employee';

type TargetAudienceKey = keyof NonNullable<RelatedEmployeeFilter> | 'assignments' | 'accounts' | 'contractType';

type TargetAudienceConfig = {
  key: TargetAudienceKey;
  label: string;
  dictionary?: Record<string, string>;
  sourceType?: 'filter' | 'direct';
};

type TargetAudienceSource = {
  relatedEmployeeFilter?: Nullable<RelatedEmployeeFilter>;
  assignments?: string[];
  accounts?: string[];
  contractType?: string[];
};

export type TargetAudienceDictionaries = {
  officesById: Record<string, string>;
  positionsById: Record<string, string>;
  projectsById: Record<string, string>;
  accountsById: Record<string, string>;
};

export type TargetAudienceDisplayPart = {
  key: TargetAudienceKey;
  label: string;
  values: string[];
};

type TargetAudienceEntry = {
  key: TargetAudienceKey;
  label: string;
  displayValue: string;
  filterValue: string;
};

const getTargetAudienceConfig = ({
  officesById,
  positionsById,
  projectsById,
  accountsById,
}: TargetAudienceDictionaries): TargetAudienceConfig[] => [
  { key: 'officeId', label: 'Office', dictionary: officesById, sourceType: 'filter' },
  { key: 'positionId', label: 'Position', dictionary: positionsById, sourceType: 'filter' },
  { key: 'businessRegistrationCountry', label: 'Business registration country', sourceType: 'filter' },
  { key: 'assignments', label: 'Project', dictionary: projectsById, sourceType: 'direct' },
  { key: 'accounts', label: 'Account', dictionary: accountsById, sourceType: 'direct' },
  { key: 'contractType', label: 'Contract type', dictionary: EMPLOYEE_CONTRACT_TYPES, sourceType: 'direct' },
];

const getTargetAudienceEntries = (
  source?: Nullable<TargetAudienceSource>,
  dictionaries: TargetAudienceDictionaries = { officesById: {}, positionsById: {}, projectsById: {}, accountsById: {} },
): TargetAudienceEntry[] => {
  if (!source) {
    return [];
  }

  return getTargetAudienceConfig(dictionaries).flatMap(({ key, label, dictionary = {}, sourceType = 'filter' }) => {
    const values =
      sourceType === 'filter'
        ? source.relatedEmployeeFilter?.[key as keyof RelatedEmployeeFilter]?.in || []
        : source[key as keyof TargetAudienceSource] || [];

    return (values as string[]).map(value => ({
      key,
      label,
      displayValue: dictionary[String(value)] || String(value),
      filterValue: `${String(key)}:${String(value)}`,
    }));
  });
};

export const getTargetAudienceDictionaries = (
  offices: OfficeType[] = [],
  positions: PositionType[] = [],
  projects: Array<{
    id: string | number;
    legacyId: string | number;
    name: string;
    accountData?: { accountId?: string | number; accountName?: string };
  }> = [],
): TargetAudienceDictionaries => ({
  officesById: Object.fromEntries(offices.map(office => [String(office.id), OFFICE_NAMES[office.name] || office.name])),
  positionsById: Object.fromEntries(positions.map(position => [String(position.id), position.name])),
  projectsById: Object.fromEntries(
    projects.flatMap(project => [
      [String(project.id), project.name],
      [String(project.legacyId), project.name],
    ]),
  ),
  accountsById: Object.fromEntries(
    projects
      .filter(project => !!project.accountData?.accountId && !!project.accountData?.accountName)
      .map(project => [String(project.accountData?.accountId), String(project.accountData?.accountName)]),
  ),
});

export const getTargetAudienceValues = (source?: Nullable<TargetAudienceSource>): string[] => {
  if (!source) {
    return [TARGET_AUDIENCE_ALL_EMPLOYEES];
  }

  const parts = getTargetAudienceEntries(source, {
    officesById: {},
    positionsById: {},
    projectsById: {},
    accountsById: {},
  }).map(entry => entry.filterValue);

  return parts.length ? parts : [TARGET_AUDIENCE_ALL_EMPLOYEES];
};

export const getTargetAudienceDisplayParts = (
  source?: Nullable<TargetAudienceSource>,
  dictionaries: TargetAudienceDictionaries = { officesById: {}, positionsById: {}, projectsById: {}, accountsById: {} },
): TargetAudienceDisplayPart[] => {
  if (!source) {
    return [];
  }

  const groupedEntries = getTargetAudienceEntries(source, dictionaries).reduce(
    (acc, entry) => {
      const groupKey = entry.label;
      const existing = acc[groupKey];

      if (!existing) {
        acc[groupKey] = {
          key: entry.key,
          label: entry.label,
          values: [entry.displayValue],
        };
      } else {
        existing.values.push(entry.displayValue);
      }

      return acc;
    },
    {} as Record<string, TargetAudienceDisplayPart>,
  );

  return Object.values(groupedEntries);
};
